import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import CertificateCard from './CertificateCard';
import { certificatesData, institutionCategories, getCertificatesByInstitution } from '../../data/certificates';
import { useLanguage } from '../../i18n';
import './CertificatesSection.css';

const CertificatesSection = () => {
  const [scrollPositions, setScrollPositions] = useState({});
  const [visibleInstitutions, setVisibleInstitutions] = useState({});
  const { t } = useLanguage();
  const [initializedCarousels, setInitializedCarousels] = useState({});
  const carouselRefs = useRef({});

  const scrollCarousel = (institutionId, direction) => {
    const carousel = carouselRefs.current[institutionId];
    if (!carousel) return;

    const cardWidth = 260; // largura do card
    const gap = 30; // gap entre cards
    const scrollAmount = cardWidth + gap;
    
    const currentScroll = carousel.scrollLeft;
    const newScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;
    
    carousel.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });

    setScrollPositions(prev => ({
      ...prev,
      [institutionId]: newScroll
    }));
  };

  const canScrollLeft = (institutionId) => {
    return (scrollPositions[institutionId] || 0) > 0;
  };

  const canScrollRight = (institutionId) => {
    const carousel = carouselRefs.current[institutionId];
    if (!carousel || !initializedCarousels[institutionId]) return false;
    
    const currentScroll = scrollPositions[institutionId] || 0;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    return currentScroll < maxScroll - 10; // 10px de margem
  };

  const initializeCarousel = (institutionId) => {
    const carousel = carouselRefs.current[institutionId];
    if (carousel) {
      requestAnimationFrame(() => {
        setScrollPositions(prev => ({
          ...prev,
          [institutionId]: carousel.scrollLeft
        }));
        setInitializedCarousels(prev => ({
          ...prev,
          [institutionId]: true
        }));
      });
    }
  };

  const toggleInstitutionVisibility = (institutionId) => {
    setVisibleInstitutions(prev => ({
      ...prev,
      [institutionId]: !prev[institutionId]
    }));
    
    // Inicializar o carousel após ele se tornar visível
    if (!visibleInstitutions[institutionId]) {
      setTimeout(() => initializeCarousel(institutionId), 350); // Aguardar a animação terminar
    }
  };

  // Inicializar carousels quando eles se tornarem visíveis
  useEffect(() => {
    const timers = [];
    Object.keys(visibleInstitutions).forEach(institutionId => {
      if (visibleInstitutions[institutionId] && !initializedCarousels[institutionId]) {
        timers.push(setTimeout(() => initializeCarousel(institutionId), 100));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [visibleInstitutions, initializedCarousels]);

  // Reinicializar quando o tamanho da janela mudar
  useEffect(() => {
    const handleResize = () => {
      Object.keys(initializedCarousels).forEach(institutionId => {
        if (initializedCarousels[institutionId] && carouselRefs.current[institutionId]) {
          setScrollPositions(prev => ({
            ...prev,
            [institutionId]: carouselRefs.current[institutionId].scrollLeft
          }));
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(initializedCarousels).length]);

  return (
    <section id="certificates" className="certificates-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="section-title">{t('certificates.title')}</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            {t('certificates.subtitle')}
          </p>
        </motion.div>

        <motion.div
          className="certificates-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          {institutionCategories.map((institution, institutionIndex) => {
            const institutionCertificates = getCertificatesByInstitution(institution.id);
            
            return (
              <motion.div
                key={institution.id}
                className="institution-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: institutionIndex * 0.05 }}
              >
                <div className="institution-header">
                  <div className="institution-info">
                    <h3 className="institution-title">{institution.label}</h3>
                    <div className="institution-count">
                      {institutionCertificates.length} certificados
                    </div>
                  </div>
                  <button
                    className="view-certificates-btn"
                    onClick={() => toggleInstitutionVisibility(institution.id)}
                  >
                    {visibleInstitutions[institution.id] ? 'Ocultar Certificados' : 'Exibir Certificados'}
                  </button>
                </div>
                
                <AnimatePresence>
                  {visibleInstitutions[institution.id] && (
                    <motion.div
                      className="certificates-content"
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                      exit={{ opacity: 0, height: 0, marginTop: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                    <div className="carousel-controls-container">
                      <div className="carousel-controls">
                        <button
                          className={`carousel-btn carousel-btn--left ${!canScrollLeft(institution.id) ? 'carousel-btn--disabled' : ''}`}
                          onClick={() => scrollCarousel(institution.id, 'left')}
                          disabled={!canScrollLeft(institution.id)}
                        >
                          <FiChevronLeft />
                        </button>
                        <button
                          className={`carousel-btn carousel-btn--right ${!canScrollRight(institution.id) ? 'carousel-btn--disabled' : ''}`}
                          onClick={() => scrollCarousel(institution.id, 'right')}
                          disabled={!canScrollRight(institution.id)}
                        >
                          <FiChevronRight />
                        </button>
                      </div>
                    </div>
                    
                    <div className="certificates-carousel-container">
                      <div 
                        className="certificates-carousel"
                        ref={el => carouselRefs.current[institution.id] = el}
                        onScroll={(e) => {
                          setScrollPositions(prev => ({
                            ...prev,
                            [institution.id]: e.target.scrollLeft
                          }));
                        }}
                      >
                        {institutionCertificates.map((certificate, certIndex) => (
                          <motion.div
                            key={certificate.id}
                            className="certificate-wrapper"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: certIndex * 0.05 }}
                          >
                            <CertificateCard certificate={certificate} />
                          </motion.div>
                        ))}
                      </div>
                    </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

              </div>
    </section>
  );
};

export default CertificatesSection;